import { useEffect } from 'react';
import type { MouseEvent } from 'react';

// ============================================================
// PRODUCT CATALOG — single source of truth for tracking values
// All prices in Euros (EUR).
// ============================================================
export type ProductKey = 'fitness365';
export const PRODUCTS: Record<ProductKey, {
  id: string;
  name: string;
  value: number;
  currency: 'EUR';
  checkoutUrl: string;
}> = {
  fitness365: {
    id: 'fitness-fonctionnel-365',
    name: 'FITNESS FONCTIONNEL 365',
    value: 19,
    currency: 'EUR',
    checkoutUrl: 'https://fitness-fonctionnel-365.impultienda.ar/checkout'
  }
};
const PIXEL_ID = '1693049799098924';
const SCRIPT_ID = 'fb-pixel-script';
// Session keys are namespaced per pixel so old flags never block new events.
const IC_SESSION_KEY = `ff365_ic_sent_${PIXEL_ID}`;
const VC_SESSION_KEY = `ff365_vc_sent_${PIXEL_ID}`;
const PURCHASE_SESSION_KEY = `ff365_purchase_sent_${PIXEL_ID}`;
/** Time given to the pixel to send the event before leaving the page. */
const NAVIGATION_DELAY_MS = 250;
type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};
type PixelWindow = Window & {
  fbq?: Fbq;
  _fbq?: Fbq;
  __ff365PixelReady?: boolean;
};
const getWindow = (): PixelWindow | null => typeof window === 'undefined' ? null : window as PixelWindow;
const getFbq = (): Fbq | null => {
  const w = getWindow();
  return w && typeof w.fbq === 'function' ? w.fbq : null;
};

/** Unique id per event — lets Meta deduplicate if server (CAPI) events are added. */
const newEventId = (name: string) => `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const sessionFlag = {
  has(key: string) {
    try {
      return window.sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  },
  set(key: string) {
    try {
      window.sessionStorage.setItem(key, '1');
    } catch {
      // In-memory guards still apply.
    }
  }
};
const productPayload = (productKey: ProductKey, value?: number) => {
  const p = PRODUCTS[productKey];
  const v = typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : p.value;
  return {
    value: v,
    currency: p.currency,
    content_ids: [p.id],
    content_name: p.name,
    content_type: 'product',
    contents: [{
      id: p.id,
      quantity: 1,
      item_price: v
    }],
    num_items: 1
  };
};

/**
 * Initializes the pixel exactly once per page load. Safe against React
 * StrictMode double effects and against another copy of the base code.
 */
const initPixel = () => {
  const w = getWindow();
  if (!w || w.__ff365PixelReady) return;
  w.__ff365PixelReady = true;

  // If another snippet already installed the pixel, don't init/PageView twice.
  if (w.fbq) return;
  const n = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod(...args);else n.queue!.push(args);
  } as Fbq;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  w.fbq = n;
  if (!w._fbq) w._fbq = n;
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = 'https://connect.facebook.net/fr_FR/fbevents.js';
    document.head.appendChild(script);
  }

  // Turn off Meta's automatic button-click detection so the ONLY checkout
  // event is the explicit InitiateCheckout below (no guessed duplicates).
  n('set', 'autoConfig', false, PIXEL_ID);
  n('init', PIXEL_ID);
  n('track', 'PageView', {}, {
    eventID: newEventId('pv')
  });
};

// ============================================================
// Tracking helpers
// ============================================================

/** InitiateCheckout — ONCE per browser session, however many CTAs are clicked. */
let icSentInMemory = false;
export const trackInitiateCheckout = (productKey: ProductKey) => {
  if (icSentInMemory || sessionFlag.has(IC_SESSION_KEY)) return;
  const fbq = getFbq();
  if (!fbq) return;
  icSentInMemory = true;
  sessionFlag.set(IC_SESSION_KEY);
  fbq('track', 'InitiateCheckout', productPayload(productKey), {
    eventID: newEventId('ic')
  });
};

/** ViewContent — once per session, when the offer comes into view. */
let vcSentInMemory = false;
const trackViewContent = (productKey: ProductKey) => {
  if (vcSentInMemory || sessionFlag.has(VC_SESSION_KEY)) return;
  const fbq = getFbq();
  if (!fbq) return;
  vcSentInMemory = true;
  sessionFlag.set(VC_SESSION_KEY);
  fbq('track', 'ViewContent', productPayload(productKey), {
    eventID: newEventId('vc')
  });
};

/** Purchase — once per session, only via the ?purchase=1 return URL. */
export const trackPurchase = (productKey: ProductKey, overrideValue?: number) => {
  if (sessionFlag.has(PURCHASE_SESSION_KEY)) return;
  const fbq = getFbq();
  if (!fbq) return;
  sessionFlag.set(PURCHASE_SESSION_KEY);
  fbq('track', 'Purchase', productPayload(productKey, overrideValue), {
    eventID: newEventId('pur')
  });
};

/**
 * Click handler for links that lead to a checkout: fires InitiateCheckout
 * (deduplicated) and delays navigation slightly so the event is delivered.
 */
let navigating = false;
export const trackCheckoutClick = (e: MouseEvent<HTMLAnchorElement>, href: string, productKey: ProductKey) => {
  try {
    trackInitiateCheckout(productKey);
  } catch {
    // Tracking must never block the purchase.
  }
  const opensNewTab = e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0;
  if (opensNewTab || !href.startsWith('http')) return;
  e.preventDefault();
  if (navigating) return; // ignore rapid double taps
  navigating = true;
  window.setTimeout(() => {
    window.location.href = href;
  }, NAVIGATION_DELAY_MS);
};

// Coming back with the browser "back" button restores the page from cache:
// allow CTAs to navigate again.
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', () => {
    navigating = false;
  });
}

// ============================================================
// MetaPixel component
// ============================================================
export function MetaPixel() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      initPixel();
    } catch {
      // Tracking must never break the landing page.
    }

    // ViewContent when the pricing card is seen
    const pricing = document.getElementById('pricing');
    let observer: IntersectionObserver | null = null;
    if (pricing && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          trackViewContent('fitness365');
          observer?.disconnect();
        }
      }, {
        threshold: 0.3
      });
      observer.observe(pricing);
    }

    // Purchase return URL: ?purchase=1 (optional &value=19)
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('purchase')) {
        const valueParam = params.get('value');
        trackPurchase('fitness365', valueParam ? Number(valueParam) : undefined);
        const url = new URL(window.location.href);
        url.searchParams.delete('purchase');
        url.searchParams.delete('value');
        window.history.replaceState(window.history.state, '', url.toString());
      }
    } catch {
      // ignore
    }
    return () => observer?.disconnect();
  }, []);
  return null;
}